import SvgRefreshCcw06 from '@unifyapps/icons/outline/RefreshCcw06';
import { ToolbarVariant } from '@unifyapps/table/types';
import type { ButtonColors } from '@unifyapps/ui/components/Button/types';
import type { IconComponentType } from '@unifyapps/ui/types';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import type { SVGProps } from 'react';

export const isUAPlatformToolbar = (toolbarVariant?: ToolbarVariant) =>
  toolbarVariant === ToolbarVariant.UAPlatform;

export const getAddButtonColor = (toolbarVariant?: ToolbarVariant): ButtonColors =>
  isUAPlatformToolbar(toolbarVariant) ? 'neutral' : 'brand';

// ua-platform trigger label, e.g. "Filter (2)" when a count is present, else "Filter".
export const getToolbarTriggerLabel = (baseLabel: string, count: number) =>
  count ? `${baseLabel} (${count})` : baseLabel;

export const uaPlatformTriggerClassName = cva(
  // radius-sm (6px) for every state of the ua-platform triggers (filter/sort/views/etc.)
  [
    'rounded-sm px-md font-medium',
    '!border-tertiary hover:!bg-utility-gray-25 active:!bg-utility-gray-25 aria-pressed:!bg-utility-gray-25',
  ],
  {
    variants: {
      open: {
        true: 'shadow-neutral-focus',
        false: '',
      },
      isApplied: {
        true: '!rounded-sm !border-secondary hover:!border-secondary !bg-utility-gray-100 hover:!bg-utility-gray-100 active:!bg-utility-gray-100 aria-pressed:!bg-utility-gray-100 !text-tertiary',
        false: 'hover:!border-utility-gray-300 aria-expanded:!border-utility-gray-300',
      },
    },
    compoundVariants: [
      {
        open: true,
        isApplied: false,
        class: '!bg-primary hover:!bg-primary',
      },
    ],
  },
);

export const getPopoverSurfaceClassName = (toolbarVariant?: ToolbarVariant) =>
  isUAPlatformToolbar(toolbarVariant) ? 'border rounded-md border-tertiary' : undefined;

export const TOOLBAR_VARIANT_OVERRIDES = {
  triggerDecoratorApplied: 'text-icon-secondary',
  triggerDecoratorIdle: 'text-icon-secondary',
  triggerChevronApplied: 'text-icon-secondary',
  triggerChevronIdle: 'text-icon-secondary',

  // FilterRefetch button + icon
  refetchButton:
    '!rounded-sm !border-utility-gray-200 hover:!border-utility-gray-300 hover:!bg-utility-gray-25',
  refetchIconSize: '!size-4',
  refetchIconIdleClassName: 'text-icon-secondary',

  refetchIcon: SvgRefreshCcw06,

  // Filter/sort menu input slots (property/operator/value selects)
  inputFocusRing: 'focus-within:!ring-brand-shadow-xs',
  inputFieldFont: '[&_input]:font-medium',

  // Small row-action icon button (filter delete, sort remove): muted gray at rest → icon-primary on hover.
  // No `!important`: a direct icon color already beats the button's inherited ghost-hover color, and the
  // group-hover (higher specificity) beats the rest color — mirrors the default delete-icon pattern.
  rowActionIcon: 'text-utility-gray-500 group-hover/small-icon-button:text-icon-primary',
  // reorder drag handle
  reorderIcon: 'text-icon-secondary',

  // Search clear icon
  searchClearIcon: 'text-icon-secondary',

  // FilterMenu chrome
  filterHeaderClassName: 'ps-lg pe-md py-sm', // default ps-xl pe-lg py-md
  filterFooterPaddingY: 'py-lg', // default py-md
  filterEditorRootPadding: 'ps-lg pe-sm pt-md pb-lg', // default px-lg py-lg
  menuButtonText: 'text-xs',
  closeButtonHidden: 'hidden',

  // SortMenu chrome
  sortHeaderClassName: 'ps-lg pe-md py-sm', // default ps-xl
  sortListPaddingY: 'py-xl', // default py-lg
  sortFooterPaddingY: 'py-lg', // default py-md
  sortAddButton: 'ps-0 py-sm text-xs',
  sortPopoverWidth: 'w-[396px]', // default w-[400px]
} as const;

// color for the trigger's start-decorator / chevron, by applied state.
export const getTriggerIconColor = (part: 'decorator' | 'chevron', isApplied: boolean) => {
  if (part === 'decorator') {
    return isApplied
      ? TOOLBAR_VARIANT_OVERRIDES.triggerDecoratorApplied
      : TOOLBAR_VARIANT_OVERRIDES.triggerDecoratorIdle;
  }
  return isApplied
    ? TOOLBAR_VARIANT_OVERRIDES.triggerChevronApplied
    : TOOLBAR_VARIANT_OVERRIDES.triggerChevronIdle;
};

// ua-platform triggers no longer show a trailing chevron — return nothing so callers
// (ViewsPopover, ColumnVisibility, etc.) render no end decorator.
export const renderUAPlatformChevronNode = (_isApplied: boolean) => undefined;

// shared start-decorator node for ua-platform triggers; extraIconProps lets callers
// pass per-icon overrides (e.g. SvgFilterLines02 strokeWidth) without re-implementing
// the size + color logic.
export const renderUAPlatformDecoratorNode = (
  Icon: IconComponentType,
  isApplied: boolean,
  extraIconProps?: SVGProps<SVGSVGElement>,
) => (
  <Icon
    className={clsx('size-3', getTriggerIconColor('decorator', isApplied))}
    {...extraIconProps}
  />
);
